<template>
  <div class="ho-dialog-mask"
       :class="{ 'is-open': visible }"
       @click.self="$emit('close')">
    <button type="button"
            class="ho-dialog-close"
            aria-label="Close dialog"
            @click="$emit('close')">
      <i class="el-icon-circle-close"></i>
    </button>

    <div class="ho-dialog-main">
      <div class="ho-dialog-left"
           @click.stop>
        <img v-if="outfit && outfit.cover"
             class="ho-dialog-cover"
             :src="outfit.cover"
             :alt="outfit.title || 'Outfit cover'"
             loading="lazy" />
      </div>

      <div class="ho-dialog-right"
           @click.self="$emit('close')">
        <article v-for="(item, index) in displayItems"
                 :key="item.id || index"
                 class="ho-dialog-item"
                 @click.stop>
          <div class="ho-dialog-item-thumb">
            <img :src="item.thumb"
                 :alt="item.title"
                 loading="lazy" />
          </div>
          <div class="ho-dialog-item-body">
            <h4 class="ho-dialog-item-title">
              {{ item.title }}
            </h4>
            <p class="ho-dialog-item-price">
              <span style="margin-right: -3px">
                {{ item.currencySymbol || '$' }}
              </span>
              {{ item.price }}
            </p>
            <span class="ho-dialog-item-btn"
                  @click="openGoods(item)">
              VIEW MORE
            </span>
          </div>
        </article>
      </div>
    </div>
  </div>
</template>

<script>
import { openPlatformBuy, reportGoodsClick } from '@/utils/goodsAction'

export default {
  name: 'HomeOutfitsDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    outfit: {
      type: Object,
      default: () => ({})
    },
    items: {
      type: Array,
      default: () => []
    },
    selectPlatform: {
      type: Object,
      default: () => ({})
    }
  },
  computed: {
    /**
     * 兼容接口字段与旧字段，统一弹窗列表渲染数据。
     */
    displayItems() {
      return (this.items || []).map((item, idx) => {
        const goodsName = item?.goodsName || item?.title || ''
        const goodsImage = item?.goodsImage || item?.thumb || ''
        const price = Number(item?.price || 0).toFixed(2)
        const currencySymbol = item?.currencySymbol || '$'
        const goodsId = item?.weidianGoodsId || item?.id || `${idx}`
        return {
          id: String(goodsId),
          title: goodsName,
          thumb: goodsImage,
          price,
          currencySymbol,
          link: this.buildGoodsLink(item),
          // 原始商品数据，仅用于购买点击统计（展示层已丢失微店字段）
          _origin: item
        }
      })
    }
  },
  data() {
    return {
      _prevBodyOverflow: ''
    }
  },
  watch: {
    visible: {
      immediate: true,
      handler(next) {
        this.syncBodyScroll(next)
      }
    }
  },
  mounted() {
    // 将弹窗挂到 body，规避父级 transform / overflow 造成的层叠上下文问题
    document.body.appendChild(this.$el)
  },
  beforeDestroy() {
    this.syncBodyScroll(false)
    if (this.$el && this.$el.parentNode) {
      this.$el.parentNode.removeChild(this.$el)
    }
  },
  methods: {
    /**
     * 优先带商品 id 跳商品页，缺失时回落到 goods 列表页。
     */
    buildGoodsLink(item) {
      const goodsId = item?.weidianGoodsId || item?.id
      if (goodsId) {
        return {
          path: '/goods',
          query: { goodsId: String(goodsId) }
        }
      }
      return '/goods'
    },

    syncBodyScroll(lock) {
      if (!document || !document.body) return
      if (lock) {
        this._prevBodyOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        return
      }
      document.body.style.overflow = this._prevBodyOverflow || ''
    },

    openGoods(goods) {
      reportGoodsClick(goods._origin || goods)
      openPlatformBuy(this.selectPlatform, goods)
    },
  }
}
</script>

<style scoped lang="scss">
.ho-dialog-mask {
  position: fixed;
  inset: 0;
  z-index: 2147483647;
  isolation: isolate;
  background: rgba(0, 0, 0, 0.82);
  display: -webkit-box;
  display: -webkit-flex;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 24px;
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  -webkit-transition: opacity 0.2s ease, visibility 0.2s ease;
  transition: opacity 0.2s ease, visibility 0.2s ease;
}

.ho-dialog-mask.is-open {
  opacity: 1;
  visibility: visible;
  pointer-events: auto;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.ho-dialog-main {
  width: min(851px, 90vw);
  display: grid;
  grid-template-columns: 1.05fr 0.95fr;
  gap: 10px;
  margin: auto 0;
}

.ho-dialog-close {
  position: fixed;
  top: 10px;
  right: 10px;
  z-index: 3;
  display: none;
  width: 32px;
  height: 32px;
  border: 0;
  padding: 0;
  background: transparent;
  color: var(--primary-color);
  font-size: 28px;
  line-height: 1;
  cursor: pointer;
}

.ho-dialog-left {
  position: relative;
  width: 483px;
  height: 483px;
  max-width: 100%;
  border: 1px solid hsl(from var(--primary-color) h s l / 0.35);
}

.ho-dialog-cover {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: fill;
}

.ho-dialog-right {
  display: -webkit-box;
  display: -webkit-flex;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  width: 358px;
  max-width: 100%;
  gap: 14px;
  padding-right: 4px;
}

.ho-dialog-item {
  position: relative;
  border: 1px solid var(--link-color);
  background: rgba(0, 0, 0, 0.42);
  display: grid;
  grid-template-columns: 72px 1fr;
  gap: 8px;
  width: 100%;
  max-width: 100%;
  padding: 8px;
  box-sizing: border-box;
}

.ho-dialog-item-thumb {
  width: 72px;
  height: 72px;
  background: rgba(255, 255, 255, 0.05);
}

.ho-dialog-item-thumb img {
  width: 100%;
  height: 100%;
  max-width: 100%;
  object-fit: cover;
}

.ho-dialog-item-body {
  min-width: 0;
}

.ho-dialog-item-title {
  margin: 0;
  font-size: 15px;
  line-height: 1.2;
  color: rgba(255, 255, 255, 0.92);
  text-transform: uppercase;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ho-dialog-item-price {
  margin: 4px 0;
  font-size: 14px;
  line-height: 1.5;
  color: var(--primary-color);
}

.ho-dialog-item-btn {
  display: -webkit-inline-box;
  display: -webkit-inline-flex;
  display: inline-flex;
  align-items: center;
  padding: 3px 7px;
  border: 1px solid hsl(from var(--primary-color) h s l / 0.85);
  border-radius: 4px;
  color: hsl(from var(--primary-color) h s l / 0.95) !important;
  text-decoration: none;
  font-size: 10px;
  line-height: 1;
}

.ho-dialog-item-no {
  position: absolute;
  right: 6px;
  top: 6px;
  color: rgba(255, 60, 60, 0.95);
  font-size: 10px;
}

@media (max-width: 900px) {
  .ho-dialog-mask {
    padding-top: 50px;
    overscroll-behavior-y: contain;
    -webkit-overflow-scrolling: touch;
  }

  .ho-dialog-main {
    grid-template-columns: 1fr;
    width: min(100%, 483px);
  }

  .ho-dialog-left {
    width: 100%;
    height: auto;
  }

  .ho-dialog-cover {
    width: 100%;
    height: auto;
  }

  .ho-dialog-right {
    justify-content: flex-start;
    width: 100%;
    padding-right: 0;
  }

  .ho-dialog-close {
    display: -webkit-inline-box;
    display: -webkit-inline-flex;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
}
</style>
